import { useTranslation } from 'react-i18next';
import { CircleDollarSign, Info, Landmark, Package } from 'lucide-react';
import { AdvanceBatchDetail } from '@/types';
import SignedAmount from './SignedAmount';

/**
 * "Upcoming Adjustment" card (DESIGN v1 §4 RTO-deductions + v2.4 reframe):
 * returned amount + returns fee line-items (red debit convention), neutral
 * bold total above an inset hairline, mini this-batch→next-cash-out timeline,
 * and the grey informational explainer strip (NOT amber — expected system
 * behavior, not a warning). Renders nothing when there is no deduction.
 */
function UpcomingAdjustmentCard({ batch }: { batch: AdvanceBatchDetail }) {
  const { t } = useTranslation();

  const returnedAmount = Number(batch.returned_amount ?? 0);
  const returnsFee = Number(batch.returns_fee ?? 0);
  const netDeduction = Number(batch.net_deduction_next_cashout ?? 0);

  if (returnedAmount <= 0 && netDeduction <= 0) return null;

  const totalDeduction = netDeduction > 0 ? netDeduction : returnedAmount + returnsFee;

  return (
    <section
      className='rounded-2xl border border-solid border-[rgb(232,230,227)] bg-white p-5'
      data-testid='upcoming-adjustment-card'
    >
      <div className='flex items-center gap-2'>
        <span className='text-[rgb(115,123,140)]'>
          <CircleDollarSign className='h-4 w-4' />
        </span>
        <h2 className='m-0 text-sm font-bold uppercase tracking-wider text-[rgb(57,57,60)]'>
          {String(t('advance_upcoming_adjustment_title'))}
        </h2>
      </div>
      <p className='m-0 mb-4 mt-1 text-[11px] text-[rgb(115,123,140)]'>
        {String(t('advance_upcoming_adjustment_subtitle'))}
      </p>

      {/* mini hand-off timeline: this batch → next cash-out */}
      <div className='mb-4 flex items-center gap-3'>
        <div className='flex flex-col items-center gap-1'>
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[rgb(245,245,243)]'>
            <Package className='h-4 w-4 text-[rgb(115,123,140)]' />
          </div>
          <span className='text-[10px] text-[rgb(115,123,140)]'>{String(t('this_batch'))}</span>
        </div>
        <div className='relative flex-1 self-start mt-4 border-t border-dashed border-[rgb(200,200,196)]'>
          <span className='absolute start-1/2 top-0 -translate-y-1/2 rtl:translate-x-1/2 ltr:-translate-x-1/2 whitespace-nowrap rounded-full border border-solid border-[rgb(232,230,227)] bg-white px-2 text-[11px] text-[rgb(115,123,140)]'>
            <SignedAmount amount={totalDeduction} mode='debit' />
          </span>
        </div>
        <div className='flex flex-col items-center gap-1'>
          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[rgb(245,245,243)]'>
            <Landmark className='h-4 w-4 text-[rgb(115,123,140)]' />
          </div>
          <span className='text-[10px] text-[rgb(115,123,140)]'>{String(t('next_cash_out'))}</span>
        </div>
      </div>

      {/* line-items (red debit convention) + neutral bold total */}
      <div>
        <div className='flex items-center justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('advance_cod_returned_amount'))}
          </span>
          <SignedAmount
            amount={returnedAmount}
            mode='debit'
            className='text-sm font-medium'
            style={{ color: 'rgb(239,68,68)' }}
          />
        </div>
        <div className='flex items-center justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('advance_cod_returns_fee'))}
          </span>
          <SignedAmount
            amount={returnsFee}
            mode='debit'
            className='text-sm font-medium'
            style={{ color: 'rgb(239,68,68)' }}
          />
        </div>
        <div className='mt-2 border-t border-solid border-[rgb(232,230,227)] pt-2'>
          <div className='flex items-center justify-between py-1'>
            <span className='text-sm font-bold text-[rgb(57,57,60)]'>
              {String(t('advance_cod_total_deduction'))}
            </span>
            {/* the total is a fact, not an alarm — bare magnitude, no sign */}
            <SignedAmount
              amount={totalDeduction}
              mode='none'
              className='text-sm font-bold text-[rgb(57,57,60)]'
            />
          </div>
        </div>
      </div>

      {/* informational explainer strip — grey, never amber */}
      <div className='mt-3 flex items-start gap-2.5 rounded-xl bg-[rgb(245,245,243)] px-4 py-3'>
        <Info className='mt-0.5 h-4 w-4 shrink-0 text-[rgb(115,123,140)]' />
        <p className='m-0 text-[12px] text-[rgb(115,123,140)]'>
          {String(t('advance_cod_deduction_explainer'))}
        </p>
      </div>
    </section>
  );
}

export default UpcomingAdjustmentCard;
